<script setup lang="ts">
import { ApiClientError } from "@shopware/api-client";
import type { ApiError } from "@shopware/api-client";
import { useTemplateRef } from "vue";

const emits = defineEmits<{
  (e: "success"): void;
  (e: "close"): void;
}>();
const router = useRouter();
const { isLoggedIn, login } = useUser();
const { t } = useI18n();
const { refreshSessionContext } = useSessionContext();
const { mergeWishlistProducts } = useWishlist();
const { pushSuccess } = useNotifications();
const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);
const loginErrors = ref<string[]>([]);

const loading = ref(false);
const formData = ref({
  username: "",
  password: "",
  remember: true,
});

const { resolveApiErrors } = useApiErrorsResolver("account_login");

const invokeLogin = async (): Promise<void> => {
  loginErrors.value = [];
  try {
    loading.value = true;
    await refreshSessionContext();
    await login(formData.value);
    emits("success");
    pushSuccess(t("account.messages.loggedInSuccess"));
    emits("close");
    mergeWishlistProducts();
  } catch (error) {
    if (error instanceof ApiClientError) {
      loginErrors.value = resolveApiErrors(error.details.errors as ApiError[]);
    }
  } finally {
    loading.value = false;
  }
};

const emailInputElement = useTemplateRef("emailInputElement");
useFocus(emailInputElement, { initialValue: true });
</script>
<template>
  <div
    id="modal-headline"
    role="form"
    title="Login form"
    aria-label="Login form"
    class="rounded-3xl bg-cust_cream py-10 px-4 xl:px-8 h-full"
  >
    <div v-if="!isLoggedIn" class="max-w-md w-full">
      <!-- <img
          class="mx-auto h-12 w-auto"
          src="assets/images/logo.svg"
          alt="logo of the shop"
        /> -->

      <h4 class="mt-6 flex items-center gap-2">
        {{ $t("plubel.account.signInLabel") }}
      </h4>
      <p>{{ $t("plubel.account.loginText") }}</p>
      <form
        class="flex flex-wrap justify-end mt-8 pt-2"
        @submit.prevent="invokeLogin"
      >
        <input
          v-model="formData.remember"
          type="hidden"
          name="remember"
          data-testid="login-remember-input"
        />
        <input
          id="email-address"
          ref="emailInputElement"
          v-model="formData.username"
          name="email"
          type="email"
          autocomplete="email"
          required
          class="inputField w-full text-start px-3 md:px-4 h-12 mb-4"
          :placeholder="$t('form.email')"
          data-testid="login-email-input"
        />

        <input
          id="password"
          v-model="formData.password"
          name="password"
          type="password"
          autocomplete="current-password"
          required
          class="inputField w-full text-start px-3 md:px-4 h-12 mb-4"
          :placeholder="$t('form.password')"
          data-testid="login-password-input"
        />
        <slot name="error">
          <div v-if="loginErrors.length" data-testid="login-errors-container">
            <div
              class="login-errors text-red-600 focus:ring-indigo-500 border-secondary-300 rounded text-sm"
            >
              <ul>
                <li v-for="error in loginErrors">
                  <i>{{ error }}</i>
                </li>
              </ul>
            </div>
          </div>
        </slot>
        <slot :data="formData" />
        <button
          class="btn-primary text-lg w-full md:w-auto md:text-22px px-8 h-12 rounded-full md:rounded-full mt-8 md:mt-10 relative"
          type="submit"
          data-testid="login-submit-button"
          :disabled="loading"
        >
          {{ $t("account.signIn") }}
          <i
            v-if="loading"
            class="fa-solid fa-spinner animate-spin text-xl ml-1"
          ></i>
        </button>

        <slot name="action"> </slot>
      </form>
    </div>
    <div v-else>
      <h2 class="mt-6 flex items-center justify-jutify gap-2">{{ $t("account.loggedInInfo") }}</h2>
    </div>
  </div>
</template>
